import React, { useRef } from 'react'
import { Box } from '@mui/material'
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import DeliveryDiningOutlinedIcon from '@mui/icons-material/DeliveryDiningOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined'
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined'
import { C, tnum } from './tokens'
import { FULFILMENT_TABS } from './model'

const ICON = {
  PICKUP: ShoppingBagOutlinedIcon,
  RESTAURANT: StorefrontOutlinedIcon,
  RUSHMYFOOD: DeliveryDiningOutlinedIcon,
  FIRST_DELIVERY: LocalShippingOutlinedIcon,
  DINE_IN: RestaurantOutlinedIcon,
  COUNTER: PointOfSaleOutlinedIcon,
}

/**
 * Second tab row: how the order reaches the customer. Combines with the status
 * tabs above (Active + Pickup = open pickups); counts follow the status tab.
 * `counts` is null while they'd be a guess (history not loaded yet). `tabs`
 * defaults to every order type.
 */
export default function TypeTabs({ tabs = FULFILMENT_TABS, value, counts, onChange }) {
  const refs = useRef([])

  const onKeyDown = (e, i) => {
    const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (!dir) return
    e.preventDefault()
    const next = (i + dir + tabs.length) % tabs.length
    if (refs.current[next]) refs.current[next].focus()
    onChange(tabs[next].key)
  }

  return (
    <Box sx={{ overflowX: 'auto', mx: -0.5, px: 0.5, py: 0.25, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
      <Box role="tablist" aria-label="Order type" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        {tabs.map((t, i) => {
          const on = t.key === value
          const n = counts ? counts[t.key] : null
          const Icon = ICON[t.key]
          return (
            <Box
              key={t.key}
              component="button"
              type="button"
              role="tab"
              aria-selected={on}
              tabIndex={on ? 0 : -1}
              ref={el => { refs.current[i] = el }}
              onClick={() => onChange(t.key)}
              onKeyDown={e => onKeyDown(e, i)}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.75, height: 30, pl: Icon ? 1 : 1.25, pr: 1, borderRadius: 999,
                font: 'inherit', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', cursor: 'pointer',
                border: `1px solid ${on ? C.ink : C.border}`,
                bgcolor: on ? C.ink : C.card,
                color: on ? '#fff' : n === 0 ? C.faint : C.text,
                transition: 'background-color 120ms, border-color 120ms, color 120ms',
                '&:hover': { borderColor: on ? C.ink : C.input },
                '&:focus-visible': { outline: 'none', boxShadow: `0 0 0 3px rgba(141,151,168,0.35)` },
                '& svg': { fontSize: 16, color: on ? '#fff' : C.muted, opacity: !on && n === 0 ? 0.5 : 1 },
              }}
            >
              {Icon && <Icon />}
              {t.label}
              {n != null && (
                <Box
                  component="span"
                  sx={{
                    minWidth: 18, px: 0.625, borderRadius: 999, textAlign: 'center', fontSize: 11, lineHeight: '16px', fontWeight: 600, ...tnum,
                    ...(on ? { bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' } : n === 0 ? { color: C.faint } : { bgcolor: C.mutedBg, color: C.muted }),
                  }}
                >
                  {n}
                </Box>
              )}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
